import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
	isRedirectableHash,
	type LogPathSortKey,
	type RequestMissPath,
} from "shared";
import { listLogs } from "@/api/logs";
import { ApiException } from "@/api/client";
import { SortHeader, type SortState } from "@/components/SortHeader";
import { UaCell } from "@/components/UaCell";
import { formatDt } from "@/lib/format";

/** Segment usable as a short-link hash? ("/wp-login.php" → no, "/zz9zz" → yes) */
function linkableSegment(path: string): string | null {
	const segment = path.startsWith("/") ? path.slice(1) : path;
	if (!segment || segment.includes("/")) return null;
	return isRedirectableHash(segment) ? segment : null;
}

export function LogsPage() {
	const [misses, setMisses] = useState<RequestMissPath[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<SortState<LogPathSortKey>>({
		key: "lastSeenAt",
		dir: "desc",
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(
		async (opts?: { append?: boolean; cursor?: string | null }) => {
			setLoading(true);
			setError(null);
			try {
				const res = await listLogs({
					q: search || undefined,
					cursor: opts?.cursor ?? undefined,
					sort: sort.key,
					order: sort.dir,
				});
				setMisses((prev) => (opts?.append ? [...prev, ...res.misses] : res.misses));
				setNextCursor(res.nextCursor);
			} catch (err) {
				setError(err instanceof ApiException ? err.message : "Failed to load");
			} finally {
				setLoading(false);
			}
		},
		[search, sort],
	);

	useEffect(() => {
		void load();
	}, [load]);

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-xl font-semibold tracking-tight">Logs</h1>
				<p className="mt-1 text-xs text-fg-muted">
					Aggregated miss log, one row per probed path. Successful requests and live
					short-link clicks are not logged.
				</p>
			</div>

			<form
				className="flex flex-wrap gap-2"
				onSubmit={(e) => {
					e.preventDefault();
					setSearch(q.trim());
				}}
			>
				<input
					type="search"
					placeholder="Search path, query or IP…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					className="flex-1 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
				/>
				<button
					type="submit"
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
				>
					Search
				</button>
			</form>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<div className="overflow-x-auto rounded-xl border border-border">
					<table className="w-full min-w-[900px] text-left text-sm">
					<thead className="border-b border-border bg-surface-raised text-fg-muted">
						<tr>
							<SortHeader
								label="Path"
								sortKey="path"
								sort={sort}
								onSort={setSort}
								defaultDir="asc"
							/>
							<th className="px-3 py-2 font-medium">Status</th>
							<th
								className="px-3 py-2 font-medium"
								title="User agent of the most recent hit"
							>
								Last UA
							</th>
							<SortHeader
								label="Hits"
								sortKey="hits"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<SortHeader
								label="IPs"
								sortKey="sources"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<SortHeader
								label="Last seen"
								sortKey="lastSeenAt"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<th className="px-3 py-2 font-medium">Actions</th>
						</tr>
					</thead>
					<tbody>
						{misses.map((m) => {
							const segment = linkableSegment(m.path);
							return (
								<tr key={m.path} className="border-b border-border last:border-0">
									<td className="max-w-[20rem] px-3 py-2">
										<a
											href={m.path}
											target="_blank"
											rel="noreferrer"
											title="Open this path"
											className="block truncate font-mono text-xs text-accent hover:underline"
										>
											{m.path}
										</a>
									</td>
									<td className="px-3 py-2">
									<span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs font-medium">
										{m.lastStatus}
									</span>
								</td>
								<UaCell ua={m.lastUserAgent} />
								<td className="px-3 py-2 font-mono text-xs tabular-nums">{m.hits}</td>
									<td className="px-3 py-2 font-mono text-xs tabular-nums">
										{m.sources}
									</td>
									<td className="px-3 py-2 text-xs text-fg-muted whitespace-nowrap">
										{formatDt(m.lastSeenAt)}
									</td>
									<td className="px-3 py-2">
										<div className="flex flex-wrap gap-2 text-xs">
											<Link
												to={`/logs/view?path=${encodeURIComponent(m.path)}`}
												className="text-accent hover:underline"
											>
												Open
											</Link>
											{segment && (
												<Link
													to={`/links/new?hash=${encodeURIComponent(segment)}`}
													className="text-fg-muted hover:text-fg"
													title={`Create short link /${segment}`}
												>
													Make link
												</Link>
											)}
										</div>
									</td>
								</tr>
							);
						})}
						{!loading && misses.length === 0 && (
							<tr>
								<td colSpan={7} className="px-3 py-8 text-center text-fg-muted">
									No misses logged yet.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{loading && <p className="text-sm text-fg-muted">Loading…</p>}

			{nextCursor && !loading && (
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
					onClick={() => void load({ append: true, cursor: nextCursor })}
				>
					Load more
				</button>
			)}
		</div>
	);
}
